'use client'

import type { ReactNode } from 'react'
import { createContext, useContext } from 'react'

/**
 * Radix renders overlays (Dialog, Sheet, Select…) into a portal attached to
 * `document.body`, which is OUTSIDE the element that carries the theme class.
 * Anything themed by a class on a wrapper therefore loses its tokens the moment
 * it is portalled. This context carries the active theme down through React —
 * which portals do preserve — so overlay content can stamp it back on itself.
 *
 * The value is a CSS CLASS NAME, and that is not a detail: the shared theme
 * layer declares
 *
 *     @custom-variant dark (&:where(.dark, .dark *));
 *
 * so the whole theming system keys off a class. Consumers stamp this value
 * through `className`, never as a `data-*` attribute — an attribute would
 * satisfy a test and style precisely nothing, since no rule in this repo reads
 * `[data-theme]`.
 *
 * Not needed when the theme class sits on `<html>` (portals mount on `<body>`,
 * i.e. still inside it) — it exists for the case where a themed WRAPPER, not
 * the document root, owns the class.
 */
const PortalThemeContext = createContext<string | undefined>(undefined)

export interface PortalThemeProviderProps {
  children: ReactNode
  /** Theme CLASS to re-apply on portalled content (e.g. `"dark"`). */
  theme?: string
}

export function PortalThemeProvider({ children, theme }: PortalThemeProviderProps) {
  return <PortalThemeContext.Provider value={theme}>{children}</PortalThemeContext.Provider>
}

export function usePortalTheme() {
  return useContext(PortalThemeContext)
}
